import React, { useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Box, CssBaseline, IconButton, InputAdornment, Paper, TextField, Typography, useTheme } from '@mui/material';
import { GrainGradient } from '@paper-design/shaders-react';
import * as Sentry from '@sentry/react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as yup from 'yup';

import api from '../../api';
import logo from '../../assets/images/ScodixLogo.png';

interface FormValues {
   email: string;
   password: string;
}

const version = process.env.REACT_APP_VERSION;

const schema = yup
   .object({
      email: yup.string().email('Not a valid Email').required('Email is a required field'),
      password: yup.string().max(255).required('Password is a required field'),
   })
   .required();

const Login = (): JSX.Element => {
   const {
      register,
      handleSubmit,
      formState: { errors, isSubmitting },
   } = useForm<FormValues>({
      resolver: yupResolver(schema),
   });
   const theme = useTheme();
   const navigate = useNavigate();
   const [showPassword, setShowPassword] = useState(false);

   const onSubmit: SubmitHandler<FormValues> = async (data) => {
      try {
         const response = await api.auth.login(data);

         if (response.accessToken !== '') {
            sessionStorage.setItem('accessToken', response.accessToken);
            try {
               sessionStorage.setItem('user', JSON.stringify(response.user));
               sessionStorage.setItem('userId', response.user.id);
            } catch (e) {
               // ignore JSON errors
            }
            Sentry.setUser({
               id: response.user.id,
               email: response.user.email,
            });
            Sentry.addBreadcrumb({ category: 'auth', message: 'User logged in', level: 'info' });
            // if (process.env.NODE_ENV !== 'development') {
            Sentry.captureMessage('User logged in', 'info');
            // }
            try {
               window.dispatchEvent(
                  new CustomEvent('session-user-updated', { detail: { userId: response.user.id } }),
               );
            } catch (_) {
               // ignore dispatch errors
            }
         }
         navigate('/upload');
      } catch (error) {
         const message =
            error instanceof Error && error.message.trim() !== ''
               ? error.message
               : 'Wrong credentials provided';
         toast.error(message, { autoClose: 8000 });
         Sentry.setUser(null);
         sessionStorage.removeItem('accessToken');
         sessionStorage.removeItem('user');
         sessionStorage.removeItem('userId');
         try {
            window.dispatchEvent(new CustomEvent('session-user-updated', { detail: { userId: null } }));
         } catch (_) {
            // ignore dispatch errors
         }
      }
   };

   return (
      <Box sx={{ display: 'flex', height: '100vh', width: '100vw' }}>
         <CssBaseline />

         {/* Logo in top-left corner */}
         <Box
            sx={{
               position: 'absolute',
               top: 23,
               left: 32,
               zIndex: 10,
               [theme.breakpoints.down('md')]: {
                  left: '50%',
                  transform: 'translateX(-50%)',
                  top: 30,
               },
            }}
         >
            <img src={logo} alt='Scodix' height={35} />
         </Box>

         {/* Left side - Login Form */}
         <Box
            sx={{
               flex: 1,
               display: 'flex',
               flexDirection: 'column',
               justifyContent: 'center',
               alignItems: 'center',
               backgroundColor: '#ffffff',
               padding: 4,
               [theme.breakpoints.down('md')]: {
                  width: '100%',
                  flex: 'none',
               },
            }}
         >
            <Paper
               elevation={0}
               sx={{
                  width: '100%',
                  maxWidth: 400,
                  background: 'transparent',
                  padding: 0,
               }}
            >
               <Typography
                  variant='h4'
                  component='h1'
                  sx={{
                     mb: 4,
                     fontWeight: 600,
                     textAlign: 'center',
                     color: '#000000',
                     [theme.breakpoints.down('md')]: {
                        mt: 6,
                     },
                  }}
               >
                  RealView
               </Typography>

               <Box component='form' noValidate onSubmit={handleSubmit(onSubmit)} sx={{ width: '100%' }}>
                  <Typography display='block' sx={{ fontWeight: 500, mb: 1 }}>
                     Email
                  </Typography>
                  <TextField
                     error={errors.email !== undefined}
                     helperText={errors?.email?.message}
                     margin='normal'
                     required
                     fullWidth
                     autoFocus
                     {...register('email')}
                     sx={{
                        marginTop: 0,
                        '& .MuiOutlinedInput-root': {
                           color: '#000000',
                           backgroundColor: '#e9f0ff',
                           borderRadius: '8px',
                           '& > input': { color: '#000000' },
                           '& fieldset': { borderColor: '#c6a563' },
                           '&:hover fieldset': { borderColor: '#c6a563' },
                           '&.Mui-focused fieldset': { borderColor: '#c6a563' },
                        },
                     }}
                  />
                  <Typography display='block' sx={{ fontWeight: 500, mb: 1, mt: 2 }}>
                     Password
                  </Typography>
                  <TextField
                     autoComplete='off'
                     error={errors.password !== undefined}
                     helperText={errors?.password?.message}
                     margin='normal'
                     required
                     fullWidth
                     type={showPassword ? 'text' : 'password'}
                     {...register('password')}
                     InputProps={{
                        endAdornment: (
                           <InputAdornment position='end'>
                              <IconButton
                                 onClick={() => setShowPassword((prev) => !prev)}
                                 edge='end'
                                 size='small'
                                 sx={{ color: '#888' }}
                              >
                                 {showPassword ? <VisibilityOff fontSize='small' /> : <Visibility fontSize='small' />}
                              </IconButton>
                           </InputAdornment>
                        ),
                     }}
                     sx={{
                        marginTop: 0,
                        '& .MuiOutlinedInput-root': {
                           color: '#000000',
                           backgroundColor: '#e9f0ff',
                           borderRadius: '8px',
                           '& > input': { color: '#000000' },
                           '& fieldset': { borderColor: '#c6a563' },
                           '&:hover fieldset': { borderColor: '#c6a563' },
                           '&.Mui-focused fieldset': { borderColor: '#c6a563' },
                        },
                     }}
                  />
                  <LoadingButton
                     type='submit'
                     fullWidth
                     loading={isSubmitting}
                     disabled={isSubmitting}
                     sx={{
                        marginTop: 4,
                        backgroundColor: '#c3a05f',
                        color: '#ffffff',
                        borderRadius: '8px',
                        height: '48px',
                        fontSize: '16px',
                        fontWeight: 600,
                        textTransform: 'none',
                        '&:hover': {
                           backgroundColor: '#b28f4e',
                           boxShadow: '0 4px 12px rgba(195, 160, 95, 0.3)',
                        },
                     }}
                     variant='contained'
                  >
                     Login
                  </LoadingButton>
               </Box>

               {/* Version info */}
               <Typography
                  sx={{
                     textAlign: 'center',
                     color: 'gray',
                     fontSize: '14px',
                     padding: '20px 5px 5px',
                     fontFamily: 'Arial',
                     mt: 4,
                  }}
               >
                  {version ? `Version ${version}` : ''}
               </Typography>
            </Paper>
         </Box>

         {/* Right side - Animated Gradient */}
         <Box
            sx={{
               flex: 1,
               display: { xs: 'none', md: 'block' },
               position: 'relative',
               overflow: 'hidden',
            }}
         >
            <GrainGradient
               style={{ height: '100%', width: '100%' }}
               colorBack='hsl(0, 0%, 0%)'
               softness={0.76}
               intensity={0.45}
               noise={0}
               shape='corners'
               offsetX={0}
               offsetY={0}
               scale={1}
               rotation={0}
               speed={1}
               colors={['hsl(14, 100%, 57%)', 'hsl(45, 100%, 51%)', 'hsl(340, 82%, 52%)']}
            />
            <Typography
               variant='h3'
               sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  color: '#ffffff',
                  fontWeight: 700,
                  lineHeight: 1.2,
                  textAlign: 'center',
                  pointerEvents: 'none',
                  whiteSpace: 'nowrap',
               }}
            >
               Experience RealView
            </Typography>
         </Box>
      </Box>
   );
};

export default Login;
